import { Info } from 'lucide-react';

import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/utils/classnames';

interface MetricSectionProps {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  variant?: 'default' | 'blue';
  tooltipTitle?: string;
  tooltipBody?: React.ReactNode;
  /**
   * Contextual text shown on the right side of the header, next to the info
   * icon. Used for date-range labels (applicants section) or applicant/channel
   * counts (spend section). When absent the info icon sits next to the title.
   */
  headerLabel?: string;
  className?: string;
  isCard?: boolean;
}

function SectionTooltipIcon({
  title,
  body,
  isBlue,
}: {
  title?: string;
  body?: React.ReactNode;
  isBlue?: boolean;
}) {
  if (!title && !body) return null;
  return (
    <Tooltip
      content={
        <div className="space-y-1">
          {title && <p className="font-medium">{title}</p>}
          {body && <p>{body}</p>}
        </div>
      }
    >
      <Info
        className={cn(
          'size-3 shrink-0',
          isBlue ? 'text-fx-blue' : 'text-fx-neutral-400',
        )}
      />
    </Tooltip>
  );
}

export function MetricSection({
  label,
  icon,
  children,
  variant = 'default',
  tooltipTitle,
  tooltipBody,
  headerLabel,
  className,
  isCard = false,
}: MetricSectionProps) {
  const isBlue = variant === 'blue';
  const hasTooltip = !!(tooltipTitle || tooltipBody);

  return (
    <div className={cn('flex flex-col', className)}>
      <div
        className={cn(
          'border-fx-neutral-200 bg-fx-neutral-100 flex items-center border-b p-3',
          headerLabel ? 'justify-between' : 'gap-2',
          isBlue && 'bg-fx-blue-50',
          isCard && 'rounded-t-lg',
        )}
      >
        {/* Left: icon + section title (+ inline tooltip icon when no headerLabel) */}
        <div className="flex items-center gap-2">
          {icon}
          <Typography
            variant="caption"
            className={cn(isBlue ? 'text-fx-blue' : 'text-fx-neutral-600')}
          >
            {label}
          </Typography>
          {!headerLabel && hasTooltip && (
            <SectionTooltipIcon
              title={tooltipTitle}
              body={tooltipBody}
              isBlue={isBlue}
            />
          )}
        </div>

        {/* Right: contextual text + tooltip icon (only when headerLabel is present) */}
        {headerLabel && (
          <div className="flex items-center gap-1">
            <Typography variant="small1" className="text-fx-neutral-600">
              {headerLabel}
            </Typography>
            {hasTooltip && (
              <SectionTooltipIcon
                title={tooltipTitle}
                body={tooltipBody}
                isBlue={isBlue}
              />
            )}
          </div>
        )}
      </div>
      <div className="divide-fx-neutral-200 flex flex-1 divide-x py-3">
        {children}
      </div>
    </div>
  );
}

interface MetricGroupProps extends MetricSectionProps {}

export function MetricGroup({ variant, className, ...rest }: MetricGroupProps) {
  return (
    <div
      className={cn(
        'border-fx-neutral-200 flex flex-col rounded-lg border bg-white',
        variant === 'blue' && 'border-fx-blue',
        className,
      )}
    >
      <MetricSection variant={variant} {...rest} />
    </div>
  );
}

interface MetricGroupItemProps {
  label: string;
  children: React.ReactNode;
}

export function MetricGroupItem({ label, children }: MetricGroupItemProps) {
  return (
    <div className="flex min-w-[140px] flex-1 flex-col items-center gap-1 px-4">
      <Typography variant="subtitle1" className="text-fx-neutral-500">
        {label}
      </Typography>
      {children}
    </div>
  );
}
